import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "./ui/button";
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { SignOut, User as UserIcon, House, BookmarkSimple, Stack, Crown, ChatText } from "@phosphor-icons/react";
import NotificationsBell from "./NotificationsBell";
import FeedbackDialog from "./FeedbackDialog";

export default function Navbar() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();

    const initials = user && user.name
        ? user.name.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()
        : "TT";

    return (
        <nav
            data-testid="main-navbar"
            className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-black/5"
        >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
                <Link
                    to={user ? "/feed" : "/"}
                    data-testid="nav-logo"
                    className="flex items-center gap-2 group"
                >
                    <div className="w-9 h-9 rounded-lg bg-[#990000] flex items-center justify-center relative overflow-hidden">
                        <span className="text-[#FFCC00] font-heading font-extrabold text-lg">T</span>
                        <span className="absolute -right-1 -bottom-1 w-3 h-3 bg-[#FFCC00] rounded-sm" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-heading font-bold text-[#0A0A0A] tracking-tight text-base">
                            TrojanTrails
                        </div>
                        <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#990000]">
                            India · USC
                        </div>
                    </div>
                </Link>

                <div className="flex items-center gap-2">
                    {user ? (
                        <>
                            <Button
                                data-testid="nav-feed-btn"
                                variant="ghost"
                                onClick={() => navigate("/feed")}
                                className="text-stone-700 hover:text-[#990000] hover:bg-[#990000]/5 hidden sm:flex"
                            >
                                <House size={18} weight="regular" className="mr-1.5" />
                                Feed
                            </Button>
                            <Button
                                data-testid="nav-bookmarks-btn"
                                variant="ghost"
                                onClick={() => navigate("/bookmarks")}
                                className="text-stone-700 hover:text-[#990000] hover:bg-[#990000]/5 hidden sm:flex"
                            >
                                <BookmarkSimple size={18} weight="regular" className="mr-1.5" />
                                Saved
                            </Button>
                            <Button
                                data-testid="nav-collections-btn"
                                variant="ghost"
                                onClick={() => navigate("/collections")}
                                className="text-stone-700 hover:text-[#990000] hover:bg-[#990000]/5 hidden md:flex"
                            >
                                <Stack size={18} weight="regular" className="mr-1.5" />
                                Trip Plans
                            </Button>
                            <NotificationsBell />
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button
                                        data-testid="nav-user-menu-btn"
                                        className="flex items-center gap-2 rounded-full border border-stone-200 hover:border-[#990000] transition-colors px-1 py-1 pr-3"
                                    >
                                        <Avatar className="w-7 h-7">
                                            <AvatarFallback className="bg-[#990000] text-[#FFCC00] text-xs font-bold">
                                                {initials}
                                            </AvatarFallback>
                                        </Avatar>
                                        <span className="text-sm font-medium text-stone-700 hidden sm:inline">
                                            {user.name}
                                        </span>
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-56">
                                    <DropdownMenuLabel className="font-normal">
                                        <div className="text-sm font-medium">{user.name}</div>
                                        <div className="text-xs text-stone-500">{user.email}</div>
                                    </DropdownMenuLabel>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        data-testid="nav-profile-link"
                                        onClick={() => navigate(`/users/${user.id}`)}
                                    >
                                        <UserIcon size={16} className="mr-2" />
                                        My Profile
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid="nav-bookmarks-link-mobile"
                                        onClick={() => navigate("/bookmarks")}
                                        className="sm:hidden"
                                    >
                                        <BookmarkSimple size={16} className="mr-2" />
                                        Saved Posts
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                        data-testid="nav-collections-link-mobile"
                                        onClick={() => navigate("/collections")}
                                        className="md:hidden"
                                    >
                                        <Stack size={16} className="mr-2" />
                                        Trip Plans
                                    </DropdownMenuItem>
                                    {user.role === "admin" && (
                                        <DropdownMenuItem
                                            data-testid="nav-admin-link"
                                            onClick={() => navigate("/admin")}
                                            className="text-[#5C0000]"
                                        >
                                            <Crown size={16} className="mr-2 text-[#FFCC00]" weight="fill" />
                                            Admin Console
                                        </DropdownMenuItem>
                                    )}
                                    <FeedbackDialog
                                        trigger={
                                            <DropdownMenuItem
                                                data-testid="nav-feedback-link"
                                                onSelect={(e) => e.preventDefault()}
                                            >
                                                <ChatText size={16} className="mr-2" />
                                                Send feedback
                                            </DropdownMenuItem>
                                        }
                                    />
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                        data-testid="nav-logout-btn"
                                        onClick={async () => { await logout(); navigate("/"); }}
                                        className="text-[#990000]"
                                    >
                                        <SignOut size={16} className="mr-2" />
                                        Logout
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </>
                    ) : (
                        <>
                            <Button
                                data-testid="nav-login-btn"
                                variant="ghost"
                                onClick={() => navigate("/login")}
                                className="text-stone-700 hover:text-[#990000] hover:bg-[#990000]/5"
                            >
                                Login
                            </Button>
                            <Button
                                data-testid="nav-register-btn"
                                onClick={() => navigate("/register")}
                                className="bg-[#990000] text-white hover:bg-[#7A0000]"
                            >
                                Join the Trojans
                            </Button>
                        </>
                    )}
                </div>
            </div>
        </nav>
    );
}
